
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserTableRow } from "./UserTableRow";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface Profile {
  id: string;
  username: string | null;
  is_admin: boolean;
  created_at: string;
  verification_status: 'none' | 'pending' | 'verified' | 'rejected';
  has_verified_email: boolean;
  is_creative: boolean;
}

interface UserTableProps {
  users: Profile[];
  isAdmin: boolean;
  currentUserId: string | null;
  isOwner: boolean;
  isUpdatingRole: string | null;
  isDeletingUser: string | null;
  isUpdatingVerification: string | null;
  onRoleUpdate: (userId: string, makeAdmin: boolean) => void;
  onDeleteUser: (userId: string) => void;
  onVerificationUpdate: (userId: string, status: 'none' | 'verified' | 'verified_creative') => void;
  title: string;
}

export function UserTable({
  users,
  isAdmin,
  currentUserId,
  isOwner,
  isUpdatingRole,
  isDeletingUser,
  isUpdatingVerification,
  onRoleUpdate,
  onDeleteUser,
  onVerificationUpdate,
  title,
}: UserTableProps) {
  const isMobile = useIsMobile();
  
  return (
    <Collapsible className="w-full">
      <div className="flex items-center justify-between pb-4">
        <CollapsibleTrigger className="flex items-center gap-2 hover:opacity-80">
          <h3 className={`font-semibold ${isMobile ? "text-base" : "text-lg"}`}>
            {title} ({users.length})
          </h3>
          <ChevronDown className="h-4 w-4" />
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent>
        <div className="rounded-md border overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={isMobile ? "px-2 w-[40%]" : "w-[35%]"}>Username</TableHead>
                {!isMobile && <TableHead className="w-[20%]">Joined</TableHead>}
                <TableHead className={isMobile ? "px-2 w-[25%]" : "w-[20%]"}>Status</TableHead>
                <TableHead className={`${isMobile ? "px-2" : ""} w-[35%] text-right`}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => {
                const isCurrentUser = user.id === currentUserId;
                const isAppOwner = isOwner && isCurrentUser;
                const showButton = !isAppOwner && (!isCurrentUser || !isAdmin);

                return (
                  <UserTableRow
                    key={user.id}
                    user={user}
                    isAdmin={isAdmin}
                    isCurrentUser={isCurrentUser}
                    isAppOwner={isAppOwner}
                    showButton={showButton}
                    isUpdatingRole={isUpdatingRole}
                    isDeletingUser={isDeletingUser}
                    isUpdatingVerification={isUpdatingVerification}
                    onRoleUpdate={onRoleUpdate}
                    onDeleteUser={onDeleteUser}
                    onVerificationUpdate={onVerificationUpdate}
                    isMobile={isMobile}
                  />
                );
              })}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isMobile ? 3 : 4} className="text-center text-muted-foreground">
                    {isAdmin ? 'No administrators found' : 'No users found'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
